import { useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import useStore from '../store/useStore';

// Snake_case → camelCase key converter
function toCamelCase(obj) {
  if (Array.isArray(obj)) return obj.map(toCamelCase);
  if (obj === null || typeof obj !== 'object') return obj;
  const result = {};
  for (const [key, value] of Object.entries(obj)) {
    const camelKey = key.replace(/_([a-z0-9])/g, (_, c) => c.toUpperCase());
    result[camelKey] = value;
  }
  return result;
}

// Tables that don't exist in Supabase yet — skip queries to avoid 406 errors.
// Remove entries from this set as you create the corresponding tables.
const DEFERRED_TABLES = new Set([
  'daily_planner', 'calorie_targets',
  'body_metrics', 'weight_log', 'nutrition_log', 'workout_log', 'lift_targets',
  'sleep_log', 'daily_summaries', 'resting_heart_rate', 'vo2_max',
  'activity_summaries', 'workout_sessions', 'exercise_history', 'bmi_log',
]);

// Fetch all rows from a table (paginate past Supabase 1000-row default)
async function fetchTable(table) {
  if (DEFERRED_TABLES.has(table)) return [];
  const PAGE = 1000;
  let all = [];
  let from = 0;
  while (true) {
    const { data, error } = await supabase.from(table).select('*').range(from, from + PAGE - 1);
    if (error) {
      console.error(`Error fetching ${table}:`, error.message);
      return toCamelCase(all);
    }
    if (!data || data.length === 0) break;
    all = all.concat(data);
    if (data.length < PAGE) break;
    from += PAGE;
  }
  return toCamelCase(all);
}

// Fetch a single-row config table
async function fetchConfig(table, defaults) {
  if (DEFERRED_TABLES.has(table)) return defaults;
  const { data, error } = await supabase.from(table).select('*').limit(1).single();
  if (error) {
    if (error.code === 'PGRST116') return defaults; // no rows found
    console.error(`Error fetching ${table}:`, error.message);
    return defaults;
  }
  return toCamelCase(data);
}

export function useDataLoader() {
  const setLoading = useStore((s) => s.setLoading);
  const hydrateStore = useStore((s) => s.hydrateStore);
  const session = useStore((s) => s.session);
  const seeded = useRef(false);

  useEffect(() => {
    if (!session || !supabase) return;

    async function loadAll() {
      setLoading(true);
      try {
        const [
          accounts, creditCards, investments, subscriptions,
          cashFlowMonthly, netWorthHistory, budgetCategories, transactions,
          habits, habitLog, dailyAssessments, tasks, taskEvents, dailyPlanner,
          bucketList, readingList, places, network,
          bodyMetrics, weightLog, nutritionLog, workoutLog, liftTargets,
          calorieTargets, paycheckSnapshots, userSettings,
          balanceSheetMonthly, taxes,
          sleepLog, dailySummaries, restingHeartRate, vo2Max,
          activitySummaries, workoutSessions, exerciseHistory, bmiLog,
        ] = await Promise.all([
          fetchTable('accounts'),
          fetchTable('credit_cards'),
          fetchTable('investments'),
          fetchTable('subscriptions'),
          fetchTable('cash_flow_monthly'),
          fetchTable('net_worth_history'),
          fetchTable('budget_categories'),
          fetchTable('transactions'),
          fetchTable('habits'),
          fetchTable('habit_log'),
          fetchTable('daily_assessments'),
          fetchTable('tasks'),
          fetchTable('task_events'),
          fetchConfig('daily_planner', { month: 'March', mantra: '', quarterlyGoals: [], threeThreeThree: { mustDos: [], shouldDos: [], niceToDos: [] } }),
          fetchTable('bucket_list'),
          fetchTable('reading_list'),
          fetchTable('places'),
          fetchTable('network'),
          fetchTable('body_metrics'),
          fetchTable('weight_log'),
          fetchTable('nutrition_log'),
          fetchTable('workout_log'),
          fetchTable('lift_targets'),
          fetchConfig('calorie_targets', { sedentary: 2075, light: 2377, moderate: 2680, heavy: 2982, goal: 2377, proteinGoal: 180, carbGoal: 250, fatGoal: 70 }),
          fetchTable('paycheck_snapshots'),
          fetchConfig('user_settings', { accountsAsOfDate: new Date().toLocaleDateString('sv-SE'), creditScore: 0, budgetConfig: {} }),
          fetchTable('balance_sheet_monthly'),
          fetchTable('taxes'),
          fetchTable('sleep_log'),
          fetchTable('daily_summaries'),
          fetchTable('resting_heart_rate'),
          fetchTable('vo2_max'),
          fetchTable('activity_summaries'),
          fetchTable('workout_sessions'),
          fetchTable('exercise_history'),
          fetchTable('bmi_log'),
        ]);

        // Post-process investments with computed fields
        const processedInvestments = investments.map((inv) => {
          const currentValue = inv.shares * inv.currentPrice;
          const totalCost = inv.shares * inv.costBasis;
          const gainLoss = currentValue - totalCost;
          const gainLossPercent = totalCost !== 0 ? (gainLoss / totalCost) * 100 : 0;
          const annualDividend = currentValue * ((inv.yieldRate || 0) / 100);
          return { ...inv, currentValue, annualDividend, gainLoss, gainLossPercent, yield: inv.yieldRate || 0 };
        });

        // Merge budget config defaults
        const budgetDefaults = { rothIra: 0, roth401k: 0, hsa: 0, lifeInsurance: 0, rent: 0, carPayment: 0, brokeragePct: 50, payFrequency: 'semi-monthly', recurringPostings: [], paycheckAccountId: null, paycheckAccountName: 'Checking', paycheckAccountType: 'account', paycheckEndDate: null };
        userSettings.budgetConfig = { ...budgetDefaults, ...(userSettings.budgetConfig || {}) };
        userSettings.hiddenAccounts = userSettings.hiddenAccounts || [];
        userSettings.hiddenCreditCards = userSettings.hiddenCreditCards || [];
        userSettings.excludeFromLiquidity = userSettings.excludeFromLiquidity || [];

        // Derive workoutLog from workoutSessions (Fitbod structured data) for UI pages
        const derivedWorkoutLog = workoutSessions.length > 0
          ? workoutSessions.map((s) => ({
              id: s.id || `ws-${s.date}`,
              date: s.date,
              type: s.type,
              exercises: s.exercises || [],
              duration: s.exerciseDetails?.reduce((sum, e) => sum + (e.totalDurationMin || 0), 0) || 0,
              notes: '',
              source: s.source || 'Workout Export',
              exerciseDetails: s.exerciseDetails,
            }))
          : workoutLog;

        hydrateStore({
          accounts, creditCards, investments: processedInvestments, subscriptions,
          cashFlowMonthly, netWorthHistory, budgetCategories, transactions,
          habits, habitLog, dailyAssessments, tasks, taskEvents, dailyPlanner,
          bucketList, readingList, places, network,
          bodyMetrics, weightLog, nutritionLog, workoutLog: derivedWorkoutLog, liftTargets,
          calorieTargets, paycheckSnapshots, userSettings,
          balanceSheetMonthly, taxes,
          sleepLog, dailySummaries, restingHeartRate, vo2Max,
          activitySummaries, workoutSessions, exerciseHistory, bmiLog,
        });

        // Guard against React strict mode double-effect
        if (seeded.current) { setLoading(false); return; }
        seeded.current = true;
      } catch (err) {
        console.error('Error loading data:', err);
      } finally {
        setLoading(false);
      }
    }

    loadAll();
  }, [session]);
}
